import { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  MapPin, ArrowLeft, ArrowRight, Sparkle, GraduationCap, Plant,
  Storefront, Buildings, Bird, House, X,
} from "@phosphor-icons/react";

/**
 * GeoTree — unified geographic drill-down for the SocialPulse ecosystem.
 *
 * Country → State → District → City → Village.  At every node the caller
 * sees a merged list of placeable entities across kinds:
 * educational institutions, farms, farm shops, food businesses,
 * government agencies and observations.
 *
 * Props
 *  - context   : "naturepulse" (default) | "farmers"
 *  - basePath  : "/geo" (default) | "/farmers/geo-tree"
 *  - defaultKinds : optional array of kind slugs to preselect
 */
const LEVELS = ["country", "state", "district", "city", "village"];

const KIND_META = {
  organization:   { label: "Institutions",   icon: GraduationCap, color: "#6d28d9" },
  farm:           { label: "Farms",          icon: Plant,         color: "#0f7a3e" },
  farm_website:   { label: "Farm Shops",     icon: Storefront,    color: "#B85F2A" },
  food_business:  { label: "Food Businesses", icon: House,        color: "#B85F2A" },
  gov_agency:     { label: "Government",     icon: Buildings,     color: "#334155" },
  observation:    { label: "Observations",   icon: Bird,          color: "#065f46" },
};

export default function GeoTree({
  context = "naturepulse",
  basePath = "/geo",
  defaultKinds = null,
  navSlot = null,
  brandLabel = "NaturePulse",
  brandAccent = "#B85F2A",
}) {
  const [params, setParams] = useSearchParams();

  const filters = useMemo(() => ({
    country:  params.get("country")  || "",
    state:    params.get("state")    || "",
    district: params.get("district") || "",
    city:     params.get("city")     || "",
    village:  params.get("village")  || "",
  }), [params]);

  const activeKinds = useMemo(() => {
    const raw = params.get("kinds");
    if (raw) return raw.split(",").filter(Boolean);
    return defaultKinds || Object.keys(KIND_META);
  }, [params, defaultKinds]);

  const [facets, setFacets] = useState({ level: "country", facets: [] });
  const [entities, setEntities] = useState({ groups: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({
      ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
      kinds: activeKinds.join(","),
    }).toString();

    Promise.all([
      http.get(`/geo/facets?${qs}`).then((r) => r.data),
      http.get(`/geo/entities?${qs}`).then((r) => r.data),
    ])
      .then(([f, e]) => { setFacets(f); setEntities(e); })
      .catch((err) => toast.error(err?.response?.data?.detail || "Load failed"))
      .finally(() => setLoading(false));
  }, [filters, activeKinds]);

  useEffect(() => { load(); }, [load]);

  const nextLevel = facets.level; // country / state / district / city / village / null

  const setLevel = (lvl, value) => {
    const p = new URLSearchParams(params);
    p.set(lvl, value);
    // clear deeper levels
    const idx = LEVELS.indexOf(lvl);
    for (let i = idx + 1; i < LEVELS.length; i++) p.delete(LEVELS[i]);
    setParams(p);
  };

  const clearLevel = (lvl) => {
    const p = new URLSearchParams(params);
    const idx = LEVELS.indexOf(lvl);
    for (let i = idx; i < LEVELS.length; i++) p.delete(LEVELS[i]);
    setParams(p);
  };

  const toggleKind = (k) => {
    const set = new Set(activeKinds);
    if (set.has(k)) set.delete(k); else set.add(k);
    const arr = Array.from(set);
    const p = new URLSearchParams(params);
    if (arr.length === 0 || arr.length === Object.keys(KIND_META).length) p.delete("kinds");
    else p.set("kinds", arr.join(","));
    setParams(p);
  };

  const crumbLevels = LEVELS.filter((l) => filters[l]);

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid="geo-tree">
      {navSlot}
      <div className="max-w-6xl mx-auto">
        {/* Hero */}
        <header>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase" style={{ color: brandAccent }}>
            <Sparkle size={12} weight="fill" className="inline mr-1" />
            {brandLabel} · Geographic Tree
          </div>
          <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
            Drill down to every farm, school & agency
          </h1>
          <p className="mt-2 max-w-2xl text-[#3A3830]">
            Country → state → district → city → village. Everything that lives on the {brandLabel} network, in one map.
          </p>
        </header>

        {/* Breadcrumbs */}
        <nav className="mt-6 flex flex-wrap items-center gap-1.5" data-testid="geo-breadcrumb">
          <Link to={basePath} onClick={() => setParams(new URLSearchParams())}
                className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[color:var(--np-brand,#B85F2A)] hover:text-[color:var(--np-brand,#B85F2A)]">
            <House size={11} weight="fill" className="inline mr-1" /> World
          </Link>
          {crumbLevels.map((l) => (
            <div key={l} className="flex items-center gap-1.5">
              <ArrowRight size={11} weight="bold" className="text-[#3A3830]" />
              <button
                onClick={() => clearLevel(l)}
                data-testid={`geo-crumb-${l}`}
                className="rounded-full border border-[color:var(--np-brand,#B85F2A)]/40 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]"
                title={`Clear ${l}`}
              >
                <MapPin size={10} weight="fill" className="inline mr-1" />
                {filters[l]}
                <X size={9} weight="bold" className="inline ml-1.5 opacity-70" />
              </button>
            </div>
          ))}
        </nav>

        {/* Kind chips */}
        <section className="mt-6" data-testid="geo-kind-filter">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830]">Include</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(KIND_META).map(([key, meta]) => {
              const Icon = meta.icon;
              const on = activeKinds.includes(key);
              return (
                <button
                  key={key}
                  onClick={() => toggleKind(key)}
                  data-testid={`geo-kind-${key}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 border font-mono-tech text-[10px] tracking-[0.28em] uppercase transition ${
                    on ? "bg-[#1B4332] text-[#F5EBDC] border-[#1B4332]"
                       : "bg-white text-[#3A3830] border-black/15 hover:border-[#1B4332]"
                  }`}
                >
                  <Icon size={11} weight="fill" /> {meta.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Next-level facets */}
        {nextLevel && (
          <section className="mt-10" data-testid="geo-facets">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase" style={{ color: brandAccent }}>
              Next level · {nextLevel}
            </div>
            <h2 className="mt-1 font-serif-display text-2xl md:text-3xl">
              {crumbLevels.length === 0 ? "Pick a country" :
               `Pick a ${nextLevel} in ${filters[crumbLevels[crumbLevels.length - 1]]}`}
            </h2>

            {loading ? (
              <div className="mt-4 text-[#3A3830]">Loading…</div>
            ) : facets.facets.length === 0 ? (
              <div className="mt-4 rounded-2xl border-2 border-dashed border-black/10 p-6 text-center text-[#3A3830]">
                No entries yet at this level.
              </div>
            ) : (
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {facets.facets.map((f) => (
                  <button
                    key={f.name}
                    onClick={() => setLevel(nextLevel, f.name)}
                    data-testid={`geo-facet-${nextLevel}-${f.name}`}
                    className="text-left rounded-2xl border border-black/10 bg-white p-4 hover:border-[color:var(--np-brand,#B85F2A)] hover:shadow-sm transition"
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-serif-display text-xl">{f.name}</div>
                      <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
                        {f.total} total
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {Object.entries(f.by_kind).map(([kind, count]) => {
                        const meta = KIND_META[kind];
                        if (!meta) return null;
                        const Icon = meta.icon;
                        return (
                          <span
                            key={kind}
                            className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase"
                            style={{ color: meta.color }}
                          >
                            <Icon size={9} weight="fill" /> {meta.label} {count}
                          </span>
                        );
                      })}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Entities at this node */}
        <section className="mt-12" data-testid="geo-entities">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase" style={{ color: brandAccent }}>
            Everything at this node
          </div>
          <h2 className="mt-1 font-serif-display text-2xl md:text-3xl">
            {crumbLevels.length === 0 ? "Pick a country to see entities" : `Placed in ${filters[crumbLevels[crumbLevels.length - 1]]}`}
          </h2>

          {crumbLevels.length === 0 ? (
            <p className="mt-3 text-[#3A3830]">Choose a country above.</p>
          ) : loading ? (
            <div className="mt-3 text-[#3A3830]">Loading…</div>
          ) : (
            <div className="mt-4 space-y-6">
              {entities.groups.filter((g) => g.count > 0).length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-black/10 p-8 text-center text-[#3A3830]">
                  No entities placed here yet. Add a school, farm, food business or government agency to seed this node.
                </div>
              ) : entities.groups.filter((g) => g.count > 0).map((g) => {
                const meta = KIND_META[g.kind] || {};
                const Icon = meta.icon || MapPin;
                return (
                  <div key={g.kind} data-testid={`geo-group-${g.kind}`}>
                    <div className="flex items-center gap-2" style={{ color: meta.color }}>
                      <Icon size={16} weight="fill" />
                      <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase">
                        {meta.label} · {g.count}
                      </div>
                    </div>
                    <div className="mt-2 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {g.entities.map((ent) => (
                        <EntityCard key={ent.id} entity={ent} kind={g.kind} linkSlug={g.link_slug} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function EntityCard({ entity, kind, linkSlug }) {
  const name =
    entity.name || entity.farm_name || entity.short_name || entity.title || "(unnamed)";
  const sub = kind === "organization" ? entity.kind
    : kind === "farm_website" ? (entity.tagline || (entity.farm_types || []).join(" · "))
    : kind === "food_business" ? entity.kind
    : kind === "gov_agency" ? entity.kind
    : kind === "farm" ? (entity.primary_crop || (entity.practices || []).join(" · "))
    : kind === "observation" ? entity.category
    : "";

  // Best-effort location line — flat vs nested location.
  const loc = entity.location || {};
  const geo = [
    entity.village || loc.village, entity.city || loc.city,
    entity.district || loc.district, entity.state || loc.state,
    entity.country || loc.country,
  ].filter(Boolean).join(", ");

  const link = linkSlug
    ? linkSlug.replace("{slug}", entity.slug || entity.id).replace("{id}", entity.id)
    : null;
  const external = kind === "farm_website" || kind === "observation";

  return (
    <Link
      to={link || "#"}
      data-testid={`geo-entity-${entity.id}`}
      className="block rounded-2xl border border-black/10 bg-white p-4 hover:border-[color:var(--np-brand,#B85F2A)] hover:shadow-sm transition"
    >
      <div className="font-serif-display text-lg leading-tight">{name}</div>
      {sub && (
        <div className="mt-0.5 font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">
          {sub}
        </div>
      )}
      {geo && (
        <div className="mt-2 flex items-center gap-1 text-xs text-[#3A3830]">
          <MapPin size={11} weight="fill" /> {geo}
        </div>
      )}
      {entity.website && (
        <div className="mt-1 text-xs text-[#3A3830] truncate">{entity.website}</div>
      )}
      <div className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
        Open <ArrowRight size={10} weight="bold" />
      </div>
    </Link>
  );
}
